<template>
  <Dialog :open="props.show" @update:open="onClose">
    <DialogContent class="sm:max-w-[500px]">
      <DialogHeader>
        <DialogTitle>Cancellation Policy</DialogTitle>
      </DialogHeader>
      <span v-if="noCancellationTerms">No cancellation policy on this order.</span>
      <div v-else class="flex flex-col gap-2">
        <p>If this payment plan is cancelled the following fees will be applied.</p>

        <template v-if="combinationType === 'Deposit + Fee'">
          <div class="grid grid-cols-2 border font-semibold border-grey-light-200 rounded-lg overflow-hidden">
            <div class="p-4 col-span-1 font-semibold bg-brand-600 text-white">Deposit</div>
            <div class="p-4 col-span-1 font-semibold text-brand-600">{{ displayCurrency(deposit) }}</div>
          </div>
          <div class="grid grid-cols-2 border font-semibold border-grey-light-200 rounded-lg overflow-hidden">
            <div class="p-4 col-span-1 font-semibold bg-brand-600 text-white">Flat Fee</div>
            <div class="p-4 col-span-1 font-semibold text-brand-600">{{ displayCurrency(flatFee) }}</div>
          </div>
        </template>

        <template v-else-if="combinationType === 'Percentage + Fee'">
          <div class="grid grid-cols-2 border font-semibold border-grey-light-200 rounded-lg overflow-hidden">
            <div class="p-4 col-span-1 font-semibold bg-brand-600 text-white">A % of the Order</div>
            <div class="p-4 col-span-1 font-semibold text-brand-600">
              {{ displayCurrency(percentageAmount) }} ({{ percentage }})
            </div>
          </div>
          <div class="grid grid-cols-2 border font-semibold border-grey-light-200 rounded-lg overflow-hidden">
            <div class="p-4 col-span-1 font-semibold bg-brand-600 text-white">Flat Fee</div>
            <div class="p-4 col-span-1 font-semibold text-brand-600">{{ displayCurrency(flatFee) }}</div>
          </div>
        </template>

        <template v-else-if="flatFee">
          <div class="grid grid-cols-2 border font-semibold border-grey-light-200 rounded-lg overflow-hidden">
            <div class="p-4 col-span-1 font-semibold bg-brand-600 text-white">Flat Fee</div>
            <div class="p-4 col-span-1 font-semibold text-brand-600">{{ displayCurrency(cancellationFee) }}</div>
          </div>
        </template>

        <template v-else-if="deposit">
          <div class="grid grid-cols-2 border font-semibold border-grey-light-200 rounded-lg overflow-hidden">
            <div class="p-4 col-span-1 font-semibold bg-brand-600 text-white">Deposit</div>
            <div class="p-4 col-span-1 font-semibold text-brand-600">{{ displayCurrency(cancellationFee) }}</div>
          </div>
        </template>

        <template v-else-if="percentage">
          <div class="grid grid-cols-2 border font-semibold border-grey-light-200 rounded-lg overflow-hidden">
            <div class="p-4 col-span-1 font-semibold bg-brand-600 text-white">A % of the Order</div>
            <div class="p-4 col-span-1 font-semibold text-brand-600">{{ displayCurrency(cancellationFee) }}</div>
          </div>
        </template>

        <div
          v-show="combination"
          class="grid grid-cols-2 border font-semibold border-grey-light-200 rounded-lg overflow-hidden"
        >
          <div class="p-4 col-span-1 font-semibold bg-grey-light-400 text-white">Total Cancellation Fee</div>
          <div class="p-4 col-span-1 font-semibold text-brand-600">{{ displayCurrency(cancellationFee) }}</div>
        </div>

        <div class="h-[2px] w-full bg-grey-light-200 my-4" />
      </div>

      <div class="grid grid-cols-2 border font-semibold border-grey-light-200 rounded-lg overflow-hidden">
        <div class="p-4 col-span-1 font-semibold bg-brand-500 text-white">Auto Cancellation</div>
        <div class="p-4 col-span-1 font-semibold text-brand-700">{{ autoCancellation ? 'On' : 'Off' }}</div>
      </div>

      <div v-if="autoCancellation">
        <template v-if="missed">
          <p v-if="missed === 1">This order will automatically be cancelled if a payment is missed.</p>
          <p v-else-if="missed > 1">This order will be automatically cancelled if {{ missed }} payments are missed.</p>
        </template>
        <p v-else-if="autoPercentage">
          This order will be automatically cancelled if {{ displayCurrency(autoAmount) }} ({{ autoPercentage }}%) of the
          order amount is not paid by the
          {{ displayDate(calculateLastPaymentDate(order?.createdAt, order?.cancellationTerms)) }}.
        </p>
      </div>
      <template v-else>
        <p>Cancellation of this order and related fees will be subject to the {{ order?.merchantName }} approval.</p>
      </template>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { displayCurrency, displayDate } from '@/utils/format.utils';
import { calculateLastPaymentDate } from '@/utils/order.utils';
import dayjs from 'dayjs';
import { ref, watch } from 'vue';
import type { ModelOrderResponse } from '@layup/api-sdk';

const emit = defineEmits(['update:show']);

const props = defineProps<{
  show: boolean;
  order?: ModelOrderResponse;
}>();

const daysToEnd = ref(0);
const combination = ref(false);
const deposit = ref(0);
const flatFee = ref(0);
const combinationType = ref('');
const percentage = ref(0);
const percentageAmount = ref(0);
const type = ref('');
const noCancellationTerms = ref(false);
const autoCancellation = ref(false);
const missed = ref(0);
const autoPercentage = ref(0);
const autoAmount = ref(0);
const cancellationFee = ref(0);

watch(
  () => props.order,
  (newOrder) => {
    if (newOrder?.cancellationTerms?.definition) {
      daysToEnd.value = Math.abs(newOrder.cancellationTerms.definition[0].end || 0);
      const policy = newOrder.cancellationTerms.definition[0].policy;
      const policyName = newOrder.cancellationTerms.definition[0].policy?.name as string;
      const policyDefinition = newOrder.cancellationTerms.definition[0].policy?.definition;
      const cancellationTerms = newOrder.cancellationTerms.definition[0];

      if (policyName === 'Deposit + Fee') {
        combination.value = true;
        flatFee.value = policyDefinition?.flatFee || 0;
        combinationType.value = policy?.name || '';
      } else if (policyName === 'Percentage + Fee') {
        combination.value = true;
        flatFee.value = policyDefinition?.flatFee || 0;
        percentage.value = policyDefinition?.percentage || 0;
        combinationType.value = policy?.name || '';
      } else if (policyDefinition?.flatFee) {
        flatFee.value = policyDefinition.flatFee;
        type.value = policyName;
      } else if (policyDefinition?.percentage) {
        percentage.value = policyDefinition.percentage;
        type.value = policyName;
      } else if (policyDefinition?.deposit) {
        // policyDefinition.deposit is boolean
        // deposit.value = policyDefinition.deposit;
        type.value = policyName;
      } else {
        noCancellationTerms.value = true;
      }

      autoCancellation.value = newOrder.cancellationTerms.active || false;

      if (cancellationTerms.type?.definition) {
        if (cancellationTerms.type?.definition.missed || cancellationTerms.type.definition.percentage) {
          missed.value = cancellationTerms.type.definition.missed || 0;
          autoPercentage.value = cancellationTerms.type.definition.percentage || 0;

          if (autoPercentage.value) {
            autoAmount.value = (newOrder.amountDue || 0) * (autoPercentage.value / 100);
          }
        }
      }
    } else {
      noCancellationTerms.value = true;
    }

    if (combinationType.value === 'Deposit + Fee') {
      deposit.value = newOrder?.depositAmount || 0;
      if (newOrder?.cancellationTerms?.definition) {
        flatFee.value = newOrder.cancellationTerms?.definition[0]?.policy?.definition?.flatFee || 0;
      }
      cancellationFee.value = (newOrder?.depositAmount || 0) + flatFee.value;
    } else if (combinationType.value === 'Percentage + Fee') {
      percentageAmount.value = (newOrder?.amountDue || 0) * (percentage.value / 100);
      cancellationFee.value = (newOrder?.amountDue || 0) * (percentage.value / 100) + flatFee.value;
    } else if (deposit.value) {
      // can't check deposit because its not on the SDK
      cancellationFee.value = newOrder?.depositAmount || 0;
    } else if (percentage.value) {
      cancellationFee.value = (newOrder?.amountDue || 0) * (percentage.value / 100);
    } else {
      cancellationFee.value = flatFee.value;
    }
  },
  { immediate: true },
);

const onClose = () => {
  emit('update:show');
};
</script>
